<template>
  <div class="activity-stage">
    <div class="steps-container">
      <div id="image-holder" class="image-holder" ref="imageHolder"></div>
      <div class="step" v-if="currentStep.id">
        <div class="td-holder" v-if="isCompleteWordStep" transition="fade">
          <div class="big-wrap bigText" :id="`section-${currentStep.id}`">
            <color-block-text
              :is-simple="true"
              :text="currentRound.completeWord"
              :answer="currentStep.answer"
              :text-step="5"
            >
            </color-block-text>
          </div>
        </div>
        <div class="td-holder" v-if="!isCompleteWordStep" transition="fade">
          <div
            class="normal-wrap normalText"
            v-for="(step, index) of getWordPartSteps"
            :id="`section-${step.id}`"
            :key="step.id"
          >
            <color-block-text :is-simple="true" :text="step.answer" :text-step="index">
            </color-block-text>
          </div>
        </div>

        <div class="curtain"></div>
      </div>

      <slot name="keyboard"></slot>
    </div>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ColorBlockText from 'components/text-displays/ColorBlockText.vue';

export default {
  components: { ColorBlockText },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      imageHolder: null,
      activityName: 'CurtainCall'
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    getWordPartSteps() {
      return this.currentRound?.steps.slice(0, -1) || [];
    },
    isCompleteWordStep() {
      return this.isLastStepInRound;
    },
    isLastWordPartStep() {
      return this.progress.stepIndex === 4;
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(({ monochromeImage, colorImage }) => {
        this.queueImage(monochromeImage.image.src);
        this.queueImage(colorImage.image.src);
      });
      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ wordAudio }) => {
        this.queueAudio(wordAudio.audio.src);
      });
      await this.downloadAudio();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    getActivityState() {
      const instructions = this.isTouchHold
        ? {
            written: 'Hold the target. Type each letter combo.',
            voice: `${this.voiceInstructionsPrefix}/curtaincall-${this.getRandomVoice()}-2.mp3`,
            delay: 5
          }
        : {
            written: 'Type each letter combo.',
            voice: `${this.voiceInstructionsPrefix}/curtaincall-${this.getRandomVoice()}-1.mp3`,
            delay: 5
          };

      const { rounds } = this.activityData;
      return {
        rounds,
        instructions
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.setImageBehindCurtain(this.currentRound.monochromeImage.image.src);
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    finishStep() {
      if (this.isCompleteWordStep) {
        const { src } = this.currentRound.wordAudio.audio;
        this.sndBellAndAfterPlayOtherHowl(this.cache.get(src));
      } else {
        this.sndBell();
      }

      this.completeStep();

      const element = document.getElementById(`section-${this.currentStep.id}`);

      if (this.isLastWordPartStep || this.isCompleteWordStep) {
        TweenMax.set(element, { css: { visibility: 'hidden' } });
        TweenMax.to('.curtain', 1, {
          css: { left: '100%' },
          onComplete: () => {
            TweenMax.to('.curtain', 1, {
              css: { left: '0px' },
              delay: 1,
              onComplete: () => {
                const imageSrc = this.isLastWordPartStep
                  ? this.currentRound.colorImage.image.src
                  : this.currentRound.monochromeImage.image.src;
                this.setImageBehindCurtain(imageSrc);

                if (this.isLastWordPartStep) this.setCurrentStep();
              }
            });
          }
        });
      } else {
        const xPosition = element.offsetLeft + element.offsetWidth;
        TweenMax.to(element, 0.5, { autoAlpha: 0 });
        TweenMax.to('.curtain', 1, { css: { left: xPosition } });
        this.setCurrentStep();
      }
    },

    handleRoundEnd() {
      if (this.isEndOfActivity) TweenMax.delayedCall(2.5, this.completeActivity);
      else TweenMax.delayedCall(2.5, this.setupNewRound);
    },

    setImageBehindCurtain(imageSrc) {
      this.$refs.imageHolder.style.backgroundImage = `url(${imageSrc || ''})`;
      this.$refs.imageHolder.style.backgroundRepeat = 'no-repeat';
      this.$refs.imageHolder.style.backgroundPosition = 'top center';
    }
  }
};
</script>
